import { useState, useRef, useEffect } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { MessageCircle, Send, X, Loader, AlertTriangle } from 'lucide-react'
import api from '../../utils/api.js'
import { useLanguage } from '../../i18n/index.jsx'
import './ChatBubble.css'

/**
 * AI chat. Renders a pill trigger inside the FloatingActions speed-dial;
 * the panel itself is portaled to <body> so it escapes the speed-dial's
 * collapse animation and stays visible if the cluster is collapsed.
 */

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ChatBubbleProps {
  triggerClassName?: string
  triggerStyle?: CSSProperties
}

export default function ChatBubble({ triggerClassName = 'chat-bubble-btn', triggerStyle }: ChatBubbleProps) {
  const [open, setOpen]         = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef       = useRef<HTMLInputElement>(null)
  const { t }          = useLanguage()

  // Auto-scroll when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100)
  }, [open])

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || loading) return

    const userMsg    = { role: 'user' as const, content: text }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setLoading(true)

    try {
      const { data } = await api.post<{ data: { reply: string } }>('/ai/chat', { messages: newMessages })
      setMessages(prev => [...prev, { role: 'assistant', content: data.data.reply }])
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string } } }
      const status = e.response?.status
      const errCode = e.response?.data?.error
      const aiDown = status === 502 || status === 503 || errCode === 'ai_unavailable'
      const errMsg = aiDown ? t('chatOllamaUnavailable') : t('chatError')
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  if (!open) {
    return (
      <button
        className={triggerClassName}
        onClick={() => setOpen(true)}
        title={t('assistant')}
        aria-label={t('assistant')}
        style={triggerStyle}
      >
        <MessageCircle size={16} />
        <span>{t('assistant')}</span>
      </button>
    )
  }

  return createPortal(
    <div className="chat-panel">
      {/* Header */}
      <div className="chat-header">
        <h3>
          {t('assistant')}
          <span
            style={{
              marginLeft: 8,
              padding: '1px 6px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--warning)',
              color: 'var(--warning)',
              fontSize: '0.65rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              verticalAlign: 'middle',
            }}
          >
            {t('chatTestingBadge')}
          </span>
        </h3>
        <button className="chat-close-btn" onClick={() => setOpen(false)} aria-label={t('close')}>
          <X size={16} />
        </button>
      </div>

      {/* Sensitive-data warning — shown every time the panel opens. The chat is
          routed to a non-EU endpoint for testing; do not paste personal data. */}
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          margin: '0 0 4px',
          padding: '10px 12px',
          background: 'var(--bg-elevated)',
          borderLeft: '3px solid var(--warning)',
          color: 'var(--text-primary)',
          fontSize: 'var(--text-sm)',
          lineHeight: 1.4,
        }}
      >
        <AlertTriangle size={16} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 1 }} />
        <span>{t('chatSensitiveWarning')}</span>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-welcome">
            {t('chatWelcome')}
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`chat-msg ${msg.role}`}>
            {msg.content}
          </div>
        ))}
        {loading && (
          <div className="chat-loading">
            <Loader size={14} className="spin" /> {t('chatThinking')}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-area">
        <input
          ref={inputRef}
          className="chat-input"
          type="text"
          placeholder={t('chatPlaceholder')}
          aria-label={t('chatPlaceholder')}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <button
          className="chat-send-btn"
          onClick={sendMessage}
          disabled={loading || !input.trim()}
        >
          <Send size={16} />
        </button>
      </div>
    </div>,
    document.body
  )
}
